import { useEffect, useRef, useState } from "react";
import { motion, type TargetAndTransition } from "framer-motion";
import type { Todo } from "../hooks/useTodos";
import { hkDateOf, hkToday } from "../lib/dates";
import { listRow } from "../motion";

/** How long the completed tick stays visible before the row is removed. */
const COMPLETE_TICK_MS = 350;
/** If the completion doesn't remove the row (slow/failed refetch), revert the
 *  tick after this grace period so it can't get stuck. */
const REVERT_GRACE_MS = 2000;

export function TodoRow({
  todo, homeName, homeIndex, onComplete, onDelete,
}: { todo: Todo; homeName?: string; homeIndex?: number; onComplete: (done: boolean) => void; onDelete: () => void }) {
  const done = todo.status === "done";
  // Pending-complete: keep the open row visible with its tick for a moment
  // after the click so the action is confirmed before the row collapses away.
  const [pendingDone, setPendingDone] = useState(false);
  const timers = useRef<number[]>([]);
  useEffect(() => () => {
    for (const t of timers.current) window.clearTimeout(t);
  }, []);
  const checked = done || pendingDone;
  const overdue = !done && !!todo.dueDate && hkDateOf(todo.dueDate) < hkToday();

  const handleCheck = () => {
    if (done) {
      onComplete(false);
      return;
    }
    // Extra clicks while the tick is showing are ignored (no cancel), so a
    // fast double-click can't make the tick vanish.
    if (pendingDone) return;
    setPendingDone(true);
    timers.current.push(window.setTimeout(() => onComplete(true), COMPLETE_TICK_MS));
    timers.current.push(window.setTimeout(() => setPendingDone(false), COMPLETE_TICK_MS + REVERT_GRACE_MS));
  };

  return (
    <motion.li className="todo-row-item" variants={listRow} exit={listRow.exit as TargetAndTransition}>
      <div className={`todo-row ${checked ? "done" : ""}`}>
        <button
          type="button"
          className={`todo-check ${checked ? "done" : ""}`}
          aria-label={done ? "Mark open" : "Mark done"}
          onClick={handleCheck}
        >
          {checked ? <CheckIcon /> : null}
        </button>
        <div className="todo-main">
          <p className="todo-title">{todo.title}</p>
          {(homeName || todo.dueDate || todo.tags.length > 0) && (
            <div className="todo-meta">
              {homeName ? (
                <span className="chip">
                  {homeIndex !== undefined ? <span className={`hdot hdot-${homeIndex % 6}`} aria-hidden /> : null}
                  {homeName}
                </span>
              ) : null}
              {todo.dueDate ? <span className={`chip ${overdue ? "chip-danger" : ""}`}>{hkDateOf(todo.dueDate)}</span> : null}
              {todo.tags.map((t) => (
                <span key={t} className="chip">{t}</span>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="btn btn-icon btn-ghost" aria-label="Delete to-do" onClick={onDelete}>
          ✕
        </button>
      </div>
    </motion.li>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
